import * as React from "react"

import { Ellipsis } from "lucide-react"

import { cn } from "../../cn"
import { Button } from "../Button/Button"
import {
  CollapsibleSearchInput,
  type CollapsibleSearchInputProps,
} from "../CollapsibleSearchInput/CollapsibleSearchInput"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"
import { Loader } from "../Loader/Loader"
import { SplitButton, type SplitButtonItem } from "../SplitButton/SplitButton"
import type { MeasuredToolbarItem } from "./computeLayout"
import type {
  ToolbarItem,
  ToolbarItemMode,
  ToolbarItemPin,
  ToolbarMenuEntry,
} from "./types"
import {
  useToolbarOverflow,
  type ToolbarMeasurements,
} from "./useToolbarOverflow"

/** Matches the `gap-2` between rendered controls. */
const TOOLBAR_GAP_PX = 8

const OVERFLOW_TESTID = "action-toolbar-overflow"

/**
 * Round each control's width up so the computed row width is never an
 * underestimate; combined with `shrink-0` visible items this guarantees an item
 * is either fully shown or fully in the overflow menu, never clipped by a
 * sub-pixel sliver.
 */
function measuredWidth(node: HTMLElement | null | undefined): number {
  if (!node) {
    return 0
  }
  return Math.ceil(node.getBoundingClientRect().width)
}

function defaultPin(item: ToolbarItem): ToolbarItemPin {
  return item.pinned ?? false
}

function hasCompactForm(item: ToolbarItem): boolean {
  switch (item.type) {
    case "search":
    case "custom":
      return true
    case "action":
    case "menu":
    case "split":
      return Boolean(item.icon)
  }
}

function canOverflowItem(item: ToolbarItem, pin: ToolbarItemPin): boolean {
  if (pin !== false) {
    return false
  }
  if (item.type === "custom") {
    return Boolean(item.overflow || item.overflowNode)
  }
  return true
}

function searchPropsForMeasure(
  search: CollapsibleSearchInputProps,
): CollapsibleSearchInputProps {
  const clone = { ...search } as Record<string, unknown>
  delete clone["data-testid"]
  return clone as CollapsibleSearchInputProps
}

/** A menu-row icon that swaps to a spinner while the row's action is in flight. */
function entryIcon(entry: {
  icon?: React.ReactNode
  loading?: boolean
}): React.ReactNode {
  return entry.loading ? (
    <Loader size="sm" className="size-4 text-current" />
  ) : (
    entry.icon
  )
}

function toSplitItems(entries: ToolbarMenuEntry[]): SplitButtonItem[] {
  return entries.map((entry) => ({
    label: entry.label,
    onClick: entry.onSelect,
    icon: entryIcon(entry),
    disabled: entry.disabled || entry.loading,
    destructive: entry.destructive,
    "data-testid": entry["data-testid"],
  }))
}

function menuEntries(entries: ToolbarMenuEntry[]): React.ReactNode {
  return entries.map((entry) => (
    <DropdownMenuItem
      key={entry.label}
      onSelect={entry.onSelect}
      disabled={entry.disabled || entry.loading}
      destructive={entry.destructive}
      data-testid={entry["data-testid"]}
      className="[&_svg]:size-4"
    >
      <span className="flex items-center gap-2">
        {entryIcon(entry)}
        {entry.label}
      </span>
    </DropdownMenuItem>
  ))
}

/**
 * The bare sizing control for an item, with no tooltip or dropdown wrapper.
 * Used both as the trigger inside `renderVisible` and, with `measure`, in the
 * hidden measurement layer (where data-testids are dropped to avoid duplicate
 * selectors).
 */
function triggerControl(
  item: ToolbarItem,
  mode: ToolbarItemMode,
  measure = false,
): React.ReactElement {
  const compact = mode === "compact"
  switch (item.type) {
    case "action":
      return (
        <Button
          variant={item.variant}
          icon={compact}
          disabled={item.disabled}
          loading={item.loading}
          onClick={item.onClick}
          data-testid={measure ? undefined : item["data-testid"]}
          tooltip={compact ? (item.tooltip ?? item.label) : undefined}
          tooltipProps={{ side: "bottom" }}
          // An item may phrase its tooltip as markup, which the button cannot
          // read a name out of; the label always is a string.
          aria-label={item.label}
        >
          {!item.loading && item.icon}
          {!compact && item.label}
        </Button>
      )
    case "menu":
      return (
        <Button
          variant={item.variant}
          icon={compact}
          disabled={item.disabled}
          loading={item.loading}
          data-testid={measure ? undefined : item["data-testid"]}
          tooltip={compact ? (item.tooltip ?? item.label) : undefined}
          tooltipProps={{ side: "bottom" }}
          // An item may phrase its tooltip as markup, which the button cannot
          // read a name out of; the label always is a string.
          aria-label={item.label}
        >
          {!item.loading && item.icon}
          {!compact && item.label}
        </Button>
      )
    case "split":
      if (compact) {
        return (
          <Button
            variant={item.variant}
            icon
            disabled={item.disabled}
            loading={item.loading}
            data-testid={measure ? undefined : item["data-testid"]}
            tooltip={item.tooltip ?? item.label}
            tooltipProps={{ side: "bottom" }}
            aria-label={item.label}
          >
            {item.icon}
          </Button>
        )
      }
      return (
        <SplitButton
          variant={item.variant}
          onClick={item.onClick}
          items={toSplitItems(item.items)}
          disabled={item.disabled}
          loading={item.loading}
          aria-label={item.label}
          data-testid={measure ? undefined : item["data-testid"]}
        >
          {!item.loading && item.icon}
          {item.label}
        </SplitButton>
      )
    case "search":
      return (
        <CollapsibleSearchInput
          {...(measure ? searchPropsForMeasure(item.search) : item.search)}
          ref={measure ? undefined : item.inputRef}
          collapsed={compact}
        />
      )
    case "custom": {
      if (measure) {
        return (
          <>
            {compact
              ? (item.measureCompact ?? item.compact)
              : (item.measureFull ?? item.full)}
          </>
        )
      }
      return <>{compact ? item.compact : item.full}</>
    }
  }
}

function renderVisible(
  item: ToolbarItem,
  mode: ToolbarItemMode,
): React.ReactNode {
  const compact = mode === "compact"
  switch (item.type) {
    case "action": {
      return triggerControl(item, mode)
    }
    case "menu": {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {triggerControl(item, mode)}
          </DropdownMenuTrigger>
          <DropdownMenuContent align={item.align ?? "end"}>
            {menuEntries(item.items)}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
    case "split": {
      if (!compact) {
        return triggerControl(item, "full")
      }
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {triggerControl(item, "compact")}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={item.onClick}
              className="[&_svg]:size-4"
            >
              <span className="flex items-center gap-2">
                {item.icon}
                {item.label}
              </span>
            </DropdownMenuItem>
            {item.items.length > 0 && <DropdownMenuSeparator />}
            {menuEntries(item.items)}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
    case "search":
    case "custom":
      return triggerControl(item, mode)
  }
}

function renderOverflow(item: ToolbarItem): React.ReactNode {
  switch (item.type) {
    case "action":
      return (
        <DropdownMenuItem
          key={item.id}
          onSelect={item.onClick}
          disabled={item.disabled || item.loading}
          data-testid={item["data-testid"]}
          className="[&_svg]:size-4"
        >
          <span className="flex items-center gap-2">
            {entryIcon(item)}
            {item.label}
          </span>
        </DropdownMenuItem>
      )
    case "menu":
      return (
        <DropdownMenuSub key={item.id}>
          <DropdownMenuSubTrigger className="[&_svg]:size-4">
            <span className="flex items-center gap-2">
              {item.icon}
              {item.label}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {menuEntries(item.items)}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )
    case "split":
      return (
        <DropdownMenuSub key={item.id}>
          <DropdownMenuSubTrigger className="[&_svg]:size-4">
            <span className="flex items-center gap-2">
              {item.icon}
              {item.label}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              onSelect={item.onClick}
              disabled={item.disabled || item.loading}
              className="[&_svg]:size-4"
            >
              <span className="flex items-center gap-2">
                {entryIcon(item)}
                {item.label}
              </span>
            </DropdownMenuItem>
            {item.items.length > 0 && <DropdownMenuSeparator />}
            {menuEntries(item.items)}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )
    case "custom":
      if (item.overflowNode) {
        return (
          <React.Fragment key={item.id}>{item.overflowNode}</React.Fragment>
        )
      }
      if (!item.overflow) {
        return null
      }
      return (
        <DropdownMenuItem
          key={item.id}
          onSelect={item.overflow.onSelect}
          disabled={item.overflow.disabled}
          destructive={item.overflow.destructive}
          data-testid={item.overflow["data-testid"]}
          className="[&_svg]:size-4"
        >
          <span className="flex items-center gap-2">
            {item.overflow.icon}
            {item.overflow.label}
          </span>
        </DropdownMenuItem>
      )
    case "search":
      return (
        <div
          key={item.id}
          className="px-1 py-1"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <CollapsibleSearchInput
            {...item.search}
            ref={item.inputRef}
            collapsed={false}
          />
        </div>
      )
  }
}

export interface ActionToolbarProps {
  items: ToolbarItem[]
  className?: string
  /** aria-label for the overflow trigger and its tooltip. Defaults to "More actions". */
  overflowLabel?: string
  /**
   * Which end of the row the controls sit against. Page headers push them to
   * the trailing edge (the default); a table's controls row starts them at the
   * leading edge, under the first column.
   */
  align?: "start" | "end"
  "data-testid"?: string
}

/**
 * A right-aligned page-header control cluster that degrades with available
 * width: every item renders with its label, then drops to icon-only, then the
 * lowest-priority items collapse into a `⋯` overflow menu.
 *
 * The width is measured per container (not the viewport), so it tracks the Sofia
 * panel and other docked panels. Items are declared via `items`; mark the
 * primary call-to-action `pinned: "full"` so it always keeps its label.
 */
const ActionToolbar = ({
  items,
  className,
  overflowLabel = "More actions",
  align = "end",
  "data-testid": dataTestId,
}: ActionToolbarProps) => {
  const measureRefs = React.useRef(new Map<string, HTMLDivElement>())
  const triggerMeasureRef = React.useRef<HTMLDivElement>(null)
  const itemsRef = React.useRef(items)
  itemsRef.current = items

  const setMeasureRef = React.useCallback(
    (key: string) => (node: HTMLDivElement | null) => {
      if (node) {
        measureRefs.current.set(key, node)
      } else {
        measureRefs.current.delete(key)
      }
    },
    [],
  )

  const getMeasurements = React.useCallback((): ToolbarMeasurements => {
    const measured: MeasuredToolbarItem[] = itemsRef.current.map(
      (item, index) => {
        const pin = defaultPin(item)
        const compactForm = hasCompactForm(item)
        const fullWidth = measuredWidth(
          measureRefs.current.get(`${item.id}:full`),
        )
        const compactWidthRaw = measuredWidth(
          measureRefs.current.get(`${item.id}:compact`),
        )
        return {
          id: item.id,
          index,
          priority: item.priority ?? 0,
          pin,
          fullWidth,
          compactWidth: compactForm ? compactWidthRaw || fullWidth : fullWidth,
          canCompact: pin !== "full" && compactForm,
          canOverflow: canOverflowItem(item, pin),
        }
      },
    )
    return {
      items: measured,
      gap: TOOLBAR_GAP_PX,
      triggerWidth: measuredWidth(triggerMeasureRef.current),
    }
  }, [])

  const signature = React.useMemo(
    () =>
      items
        .map((item) => {
          const label = "label" in item ? item.label : ""
          const disabled = "disabled" in item ? item.disabled : false
          const loading = "loading" in item ? item.loading : false
          return `${item.id}:${item.type}:${defaultPin(item)}:${label}:${disabled}:${loading}:${item.priority ?? 0}`
        })
        .join("|"),
    [items],
  )

  const { containerRef, layout } = useToolbarOverflow(
    items,
    getMeasurements,
    signature,
  )

  const itemById = React.useMemo(() => {
    const map = new Map<string, ToolbarItem>()
    for (const item of items) {
      map.set(item.id, item)
    }
    return map
  }, [items])

  const visibleEntries = layout.visible
    .map((visible) => ({ mode: visible.mode, item: itemById.get(visible.id) }))
    .filter((entry): entry is { mode: ToolbarItemMode; item: ToolbarItem } =>
      Boolean(entry.item),
    )

  const overflowItems = layout.overflow
    .map((id) => itemById.get(id))
    .filter((item): item is ToolbarItem => Boolean(item))

  let suffixStart = visibleEntries.length
  for (let i = visibleEntries.length - 1; i >= 0; i--) {
    if (defaultPin(visibleEntries[i].item) === "full") {
      suffixStart = i
    } else {
      break
    }
  }
  const leading = visibleEntries.slice(0, suffixStart)
  const trailing = visibleEntries.slice(suffixStart)

  const overflowTrigger =
    overflowItems.length > 0 ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            icon
            tooltip={overflowLabel}
            tooltipProps={{ side: "bottom" }}
            data-testid={OVERFLOW_TESTID}
          >
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end">
          {overflowItems.map(renderOverflow)}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null

  return (
    <div
      ref={containerRef}
      className={cn("relative min-w-0 flex-1", className)}
      data-testid={dataTestId}
    >
      <div
        className={cn(
          "flex min-w-0 items-center gap-2 overflow-hidden",
          align === "start" ? "justify-start" : "justify-end",
        )}
      >
        {leading.map(({ item, mode }) => (
          <div key={item.id} className="flex shrink-0">
            {renderVisible(item, mode)}
          </div>
        ))}
        {overflowTrigger && (
          <div className="flex shrink-0">{overflowTrigger}</div>
        )}
        {trailing.map(({ item, mode }) => (
          <div key={item.id} className="flex shrink-0">
            {renderVisible(item, mode)}
          </div>
        ))}
      </div>
      <div
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex items-center gap-2 whitespace-nowrap"
      >
        {items.map((item) => (
          <React.Fragment key={item.id}>
            <div className="inline-flex" ref={setMeasureRef(`${item.id}:full`)}>
              {triggerControl(item, "full", true)}
            </div>
            <div
              className="inline-flex"
              ref={setMeasureRef(`${item.id}:compact`)}
            >
              {triggerControl(item, "compact", true)}
            </div>
          </React.Fragment>
        ))}
        <div className="inline-flex" ref={triggerMeasureRef}>
          <Button variant="secondary" icon tooltip={overflowLabel}>
            <Ellipsis />
          </Button>
        </div>
      </div>
    </div>
  )
}

ActionToolbar.displayName = "ActionToolbar"

export { ActionToolbar }
